import { cloneDeep, without } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';

import Icon from '@ant-design/icons';
import TreeIcon from '@icons/sankey/tree-icon.svg';
import { IAutoBreakdown } from '@src/emp-3.0/components/Sankey/Configurations/AutoBreakdown/typed';
import { CustomDropdownDualSelectWithRequest } from '@src/emp-3.0/components/Sankey/components/CompactDualSelect';
import { IEventFilter } from '@src/emp-3.0/types';
import { addEventNameToFilters } from '@src/emp-3.0/components/Sankey/utils';

const AutoBreakdown: React.FC<IAutoBreakdown> = ({
  data,
  filters,
  interval,
  dimensionNameOptions,
  fixedBreakdowns: fixedBreakdownsProps,
  onDimensionChange,
}) => {
  const [breakdowns, setBreakdowns] = useState<IEventFilter[][]>([]);
  const [fixedBreakdowns, setFixedBreakdowns] = useState<IEventFilter[][]>([]);

  useEffect(() => {
    if (data?.breakdowns) {
      setBreakdowns(data?.breakdowns);
    }
  }, [data?.breakdowns]);

  useEffect(() => {
    if (fixedBreakdownsProps) {
      setFixedBreakdowns(cloneDeep(fixedBreakdownsProps));
    }
  }, [fixedBreakdownsProps]);

  if (!data || data?.breakdowns?.length < 1) return <></>;

  const handleChange = (value, index) => {
    const copiedBreakdownEventData = data;
    copiedBreakdownEventData.breakdowns[index] = value;
    onDimensionChange?.(copiedBreakdownEventData);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: 16 }}>
      <div style={{ color: 'rgba(0, 0, 0, 0.45)', letterSpacing: '-0.12px', fontSize: '12px' }}>
        Auto-breakdown
      </div>
      {breakdowns?.map((item, index) => {
        const field = item[0].field;
        const value = item[0].value;
        const is_all = item[0].is_all;
        const fixedValues =
          fixedBreakdowns?.find((breakdown) => {
            return breakdown[0].field === field;
          })?.[0]?.value ?? [];
        const filtersWithBreakdownEvent = addEventNameToFilters(filters, data.event_name);

        return (
          <CustomDropdownDualSelectWithRequest
            key={`AutoBreakdown_${field}`}
            style={{ width: '100%' }}
            showDelete={false}
            filters={filtersWithBreakdownEvent}
            interval={interval}
            disabledValueOptions={isEqual(value, []) ? [] : fixedValues}
            allChecked={is_all}
            onAllCheckedChange={(checked) => {
              let updatedBreakdown;
              if (checked) {
                updatedBreakdown = [{ ...item[0], is_all: checked, value: [] }];
              } else {
                updatedBreakdown = [{ ...item[0], is_all: checked, value: fixedValues }];
              }
              handleChange(updatedBreakdown, index);
            }}
            dimensionSelectProps={{
              value: field,
              disabled: true,
              options: dimensionNameOptions,
              onChange: (value, option) => {
                const updatedBreakdown = [{ ...item[0], field: value, value: [], is_all: true }];
                handleChange(updatedBreakdown, index);
              },
              style: {
                minWidth: '80px',
              },
            }}
            dimensionValueSelectProps={{
              mode: 'multiple',
              value: value,
              optionLabelProp: 'value',
              onChange: (value: string[], option) => {
                let updatedValue = value;
                if (value.length === 0) {
                  updatedValue = fixedValues;
                } else if (!value.includes(fixedValues[0])) {
                  updatedValue = [...fixedValues, ...value];
                }
                const isAll = isEqual(value, ['All Values']);
                const valueWithoutAll = without(updatedValue, 'All Values');

                let updatedBreakdown;
                if (isAll) {
                  updatedBreakdown = [{ ...item[0], value: valueWithoutAll, is_all: true }];
                } else {
                  updatedBreakdown = [{ ...item[0], value: valueWithoutAll, is_all: false }];
                }
                handleChange(updatedBreakdown, index);
              },
            }}
            prefix={
              <Icon
                component={TreeIcon}
                style={{
                  backgroundColor: '#f5f5f5',
                  fontSize: '10px',
                  border: '1px solid #d9d9d9',
                  borderRight: 0,
                  paddingLeft: '8px',
                  display: 'inline-flex',
                }}
              />
            }
          />
        );
      })}
    </div>
  );
};

export default AutoBreakdown;
